import React, { useCallback, useMemo } from "react";
import { useField, useFormikContext } from "formik";
import * as Yup from "yup";
import { cn } from "utils/helpers";
import { getInputClasses } from "./inputStyles";
import FormikFieldError from "../FormikFieldError";
import LabelWithGuideline from "../LabelWithGuideline";

/**
 * Built-in phone number validation. Schema is constructed once per
 * component render via useMemo so the field-level validator we hand to
 * useField has a stable identity across renders. The regex accepts the
 * Australian formats (mobile + landline, with or without the +61 prefix,
 * with optional spaces/dashes/parens) that the rest of the app uses.
 *
 * When `showCountryCode` is false, the prefix portion is hidden from the
 * user but still concatenated into field.value as the default AU prefix
 * — the regex matches either form so we don't need a separate branch.
 */
const PHONE_REGEX =
  /^(?=.*)((?:\+?61) ?(?:\((?=.*\)))?([2-47-8])\)?|(?:\((?=.*\)))?([0-1][2-47-8])\)?) ?-?(?=.*)((\d{1} ?-?\d{3}$)|(00 ?-?\d{4} ?-?\d{4}$)|( ?-?\d{4} ?-?\d{4}$)|(\d{2} ?-?\d{3} ?-?\d{3}$))/;

const COUNTRY_CODES = [
  { code: "+61", country: "AU", label: "🇦🇺 +61" },
  { code: "+64", country: "NZ", label: "🇳🇿 +64" },
  { code: "+1", country: "US", label: "🇺🇸 +1" },
  { code: "+44", country: "GB", label: "🇬🇧 +44" },
  { code: "+65", country: "SG", label: "🇸🇬 +65" },
] as const;

const DEFAULT_COUNTRY_CODE = "+61";

const splitE164 = (e164: string): { number: string; prefix: string; } => {
  for (const c of COUNTRY_CODES) {
    if (e164.startsWith(c.code)) {
      return { prefix: c.code, number: e164.slice(c.code.length) };
    }
  }
  return { prefix: DEFAULT_COUNTRY_CODE, number: e164.replace(/^\+\d{1,3}/, "") };
};

interface FormikPhoneInputProps {
  name: string;
  className?: string;
  label?: string;
  required?: boolean;
  showCountryCode?: boolean;
  /**
   * Optional external validator chained AFTER the built-in phone-format
   * check. Use for cross-field rules (e.g. "phone must differ from
   * emergency contact"). Returns undefined on success or an error string.
   */
  validate?: (value: string) => string | undefined;
}

export const FormikPhoneInput: React.FC<FormikPhoneInputProps> = ({
  name,
  label,
  required = false,
  className,
  showCountryCode = false,
  validate: externalValidate,
}) => {
  // Self-validating field-level validator. We don't accept a Yup schema
  // from the consumer for the *built-in* rule because every caller wants
  // the same phone-shape check — keeping it captive here means the
  // signature for callers is just `<FormikPhoneInput name="phone" />`.
  // External validators still chain via the `validate` prop above.
  const validatePhone = useMemo(() => {
    const schema = Yup.string()
      .required("Phone number is required")
      .matches(PHONE_REGEX, "Enter a valid phone number");
    return (value: string): string | undefined => {
      try {
        schema.validateSync(value ?? "");
      } catch (err: any) {
        return err?.message ?? "Enter a valid phone number";
      }
      return externalValidate ? externalValidate(value) : undefined;
    };
  }, [externalValidate]);

  const [field, meta] = useField<string>({ name, validate: validatePhone });
  const { setFieldValue } = useFormikContext();

  const { prefix, number } = splitE164(field.value ?? "");
  const hasError = !!(meta.error && meta.touched);

  const handlePrefixChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFieldValue(name, `${e.target.value}${number}`);
    },
    [name, number, setFieldValue]
  );

  const handleNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/[^\d\s\-()]/g, "");
      const value = showCountryCode ? `${prefix}${digits}` : `${digits}`;
      setFieldValue(name, value);
    },
    [name, prefix, setFieldValue, showCountryCode]
  );

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <LabelWithGuideline htmlFor={name} label={label} required={required} />
      <div className="flex gap-2">
        {showCountryCode && <select
          value={prefix}
          onChange={handlePrefixChange}
          onBlur={field.onBlur}
          className={cn(
            "rounded-md border px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
            hasError ? "border-red-400" : "border-gray-300"
          )}
          data-testid="phone-country-select"
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>}
        <input
          type="tel"
          value={number}
          onChange={handleNumberChange}
          onBlur={field.onBlur}
          placeholder="0400 000 000"
          className={getInputClasses(hasError, `h-14 max-h-14`)}
          data-testid="phone-input"
        />
      </div>
      <FormikFieldError text={meta.error} />
    </div>
  );
};
